import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { branch, compose, renderNothing } from 'recompose';

import { translator } from 'mage-i18n';
import { defaultContainerPropTypes, moetPropTypes, HorizontalWrapper } from 'moet-core';
import { SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  StakeholderFormCard,
} from '@/components';
import { logEvent } from '@/utils/analytics';
import { OPTIONS_META } from '@/constants/countries';
import { CAPS } from '@/constants';
import {
  STAKEHOLDER_ROLES_V2,
  stakeholderUtils,
  withStakeholderLogic,
} from '@/containers/withStakeholderLogic';
import { getCountries, getUsStates } from '@/selectors/options';
import { getMerchantCountryKey, getMerchantAwareTranslator } from '@/selectors/merchant';
import { getDefaultState } from '../selectors';

import { CY_SOLE_TRADER_IDS } from '../constants';
import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.us_sole_proprietorship';

export class USSoleProprietorship extends Component {
  constructor(props) {
    super(props);

    this.getBody = this.getBody.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  getBody() {
    const { mid, stakeholders } = this.props;

    return {
      payload: {
        params: {
          mid,
        },
        body: stakeholderUtils.getStakeholdersMappedValues(stakeholders),
      },
    };
  }

  onNext() {
    const { stakeholders, roleConstraints, onNextWrapper } = this.props;

    const isValid =
      stakeholderUtils.isStakeholderConstraintsValid(stakeholders, roleConstraints) &&
      stakeholderUtils.isStakeholdersValid(stakeholders);

    onNextWrapper(this.getBody(), isValid);
  }

  render() {
    const {
      error,
      hasBack,
      isDisabled,
      isLoading,
      onBack,
      onBlurStakeholder,
      onChangeStakeholder,
      onTouchPersonalId,
      stakeholders,
      t,
      tMerchantAware,
    } = this.props;
    const stakeholder = stakeholders[0];

    const backButton = (
      <>
        <BackButton tabIndex={15} onClick={onBack} disabled={isDisabled}>
          {t(`${tPrefix}.buttons.back`)}
        </BackButton>
        <SpacerHorizontal medium />
      </>
    );

    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
        info={tMerchantAware(`${tPrefix}.infobox`)}
      >
        <StakeholderFormCard
          autoFocus
          tabIndexStart={1}
          stakeholder={stakeholder}
          onBlur={key => onBlurStakeholder(0, key)}
          onChange={(key, value) => onChangeStakeholder(0, key, value)}
          onTouchPersonalId={() => onTouchPersonalId(0)}
          isDisabled={isDisabled}
        />

        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && backButton}
          <CallToActionButton
            tabIndex={14}
            onClick={this.onNext}
            loading={isLoading}
            disabled={isDisabled}
            {...generateCyElemId(CY_SOLE_TRADER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

USSoleProprietorship.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  forceCountry: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  countries: PropTypes.array,
  usRegions: PropTypes.array,
};

const WrappedComponent = compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    countries: getCountries(state),
    defaultState: getDefaultState(state),
    forceCountry: getMerchantCountryKey(state),
    usRegions: getUsStates(state),
  })),
  branch(
    ({ countries = false, currencies = false }) => !countries.length > 0 && !currencies.length > 0,
    renderNothing,
  ),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT, {
    capabilityCallback: () => logEvent(ANALYTICS_EVENTS.soleProprietorship),
    extraProps: {
      overrideStakeholders: true,
      defaultRole: STAKEHOLDER_ROLES_V2.OWNER,
      roleConstraints: {
        [STAKEHOLDER_ROLES_V2.OWNER]: {
          min: 1,
          max: 1,
        },
      },
    },
  }),
)(USSoleProprietorship);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.variantId = OPTIONS_META.SOLE_TRADER_KIND.OPTIONS.SOLE_PROPRIETORSHIP;

WrappedComponent.fieldsToFill = [
  [
    {
      fieldName: 'stakeholders',
      roles: [STAKEHOLDER_ROLES_V2.OWNER],
    },
  ],
];

export default WrappedComponent;
